import { FeatureId } from '@upright/common/iam/access/feature-access/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import {
  defaultAllSearchScope,
  internalAllSearchScope,
  type ModelEntitySearchResponseItem,
} from '@upright/common/model/search/types';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { buildChatUrl } from '@upright/ui/chat/chat-url';
import { useAskAiEntryPoint } from '@upright/ui/chat/use-ask-ai-entry-point';
import { useHasAccessToFeature, useIsTeam } from '@upright/ui/common/generic/hooks';
import { useTrackableSearchInputs } from '@upright/ui/common/upright/model/output/search/use-trackable-inputs';
import InterfaceSearch from '@upright/ui/icons/img/streamline-mini-line/interface-essential/search/interface-search.svg';
import { getModelEntityUrl } from '@upright/ui/untidy/urls';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { HOME_BACK_TARGET, PROFILE_BACK_STATE_KEY } from './company-profile-route';
import { useNavigationOrganizationId } from './profile-back-trail';
import { withProfileOrganization } from './profile-organization-context';
import { type SearchPanelAnchor, UnifiedShellSearchDialog } from './unified-shell-search-dialog';
import { type InternalPageHit, searchFilters, type SearchFilterId } from './unified-shell-search-results';
import styles from './unified-shell-search.module.css';
import topBarStyles from './unified-top-bar.module.css';

/**
 * The global search in the unified shell's top bar: find a fund, company or product by name and go
 * to it — a different intent from asking the chat a question, and the one control whose absence would
 * make the new shell a downgrade (PLT-6531).
 *
 * The field in the bar is the trigger. Clicking it, or ⌘K / Ctrl+K anywhere (Ctrl+S too, outside
 * text fields, the dark header's old shortcut), opens the search panel over it (PLT-7168, `unified-shell-search-dialog.tsx`):
 * the panel's own field, a type filter, results in sections, and an "Ask Upright AI" row for users who
 * can chat. Customers and the team get the same panel. A team member also gets a "Team details"
 * switch in it (PLT-7169), off by default, which adds the team-only types, the internal facts on each
 * row and the internal pages; with it off they see what a customer sees, so the team dog-foods the
 * customer's search.
 *
 * What this component keeps across opens and closes is the search-attempt tracking that feeds GTM,
 * sent on a pick and on abandonment alike, the team switch, and the selected organisation
 * (PLT-6963), which an opened profile and its Back keep.
 */
export function UnifiedShellSearch({ variant = 'bar' }: { variant?: SearchVariant }) {
  const navigate = useNavigate();
  const organizationId = useNavigationOrganizationId();
  const isTeam = useIsTeam();
  const canSearchProducts = useHasAccessToFeature(FeatureId.PRODUCT_SEARCH);
  const { hidden: chatHidden } = useAskAiEntryPoint('public');
  const trigger = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<SearchPanelAnchor>();
  const [filter, setFilter] = useState<SearchFilterId>('all');
  const { input, setInput, sendAttempts, discardAttempts } = useTrackableSearchInputs(false);
  const [storedTeamDetails, setStoredTeamDetails] = useStoredTeamDetails();
  const teamDetails = isTeam && storedTeamDetails;

  // The customer's scope is the dark header's default without phenomena; team details search every type.
  const everythingScope = teamDetails
    ? internalAllSearchScope
    : defaultAllSearchScope.filter(type => type !== ModelEntityType.PHENOMENON);
  const filters = searchFilters({ canSearchProducts, teamDetails });

  function changeTeamDetails(next: boolean) {
    setStoredTeamDetails(next);
    // A team-only chip has nothing to narrow once the switch takes its types away.
    if (!next && filters.find(option => option.id === filter)?.teamOnly === true) {
      setFilter('all');
    }
  }

  // Every setter it calls is stable, so one function serves the shortcut listener for the component's life.
  const open = useCallback(() => {
    setAnchor(measureAnchor(trigger.current));
    setIsOpen(true);
  }, []);

  function close() {
    setIsOpen(false);
    setInput('');
    setFilter('all');
  }

  /*
   * ⌘K / Ctrl+K opens the panel from anywhere, a text field included: Home puts the focus in the
   * chat composer, and a shortcut that did nothing there would do nothing on the page people land on.
   * The chord types nothing into a field, so taking it costs the field nothing. hotkeys-js cannot do
   * this (it ignores key presses in text fields), so it is a plain listener, in the capture phase on
   * the window and stopping there, so the hotkeys-js bindings on `document` never see the chord. The
   * company view's team shortcuts used Ctrl+K for one internal view, which now has Ctrl+B
   * (`company-view/internal/shared.tsx`).
   *
   * Only the bar's instance answers. The drawer mounts a second instance below `md`, and the bar's
   * stays mounted (hidden) beside it, so two listeners would open two panels.
   */
  useEffect(() => {
    if (variant !== 'bar') {
      return undefined;
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      // The modifiers are checked first: this runs on every keydown in the window, and the keydown
      // events browser autofill sends carry no `key` at all.
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      if (event.code === 'KeyK' || event.key.toLowerCase() === 'k') {
        event.preventDefault();
        event.stopPropagation();
        open();
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [variant, open]);

  // Ctrl+S, the dark header's shortcut, keeps hotkeys-js's rule: outside text fields only.
  useHotkeys(
    'ctrl+s',
    event => {
      if (variant !== 'bar') {
        return;
      }
      event.preventDefault();
      open();
    },
    [variant, open],
  );

  // The panel is placed off the field, so it follows the field when the window is resized.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const remeasure = () => {
      setAnchor(measureAnchor(trigger.current));
    };
    window.addEventListener('resize', remeasure);
    return () => {
      window.removeEventListener('resize', remeasure);
    };
  }, [isOpen]);

  function openHit(hit: ModelEntitySearchResponseItem) {
    sendAttempts({ entityRef: { type: hit.type, id: hit.id } });
    close();
    // A search starts a fresh trail, so the profile's Back is Home rather than the page searched from.
    navigate(withProfileOrganization(getModelEntityUrl(hit.type, hit.id, hit.name), organizationId), {
      state: {
        [PROFILE_BACK_STATE_KEY]: {
          ...HOME_BACK_TARGET,
          href: withProfileOrganization('/', organizationId),
        },
      },
    });
  }

  // Neither the AI row nor an internal page is an entity, and an attempt sent without one is recorded
  // as a search that found nothing, so these two picks send nothing.
  function ask(query: string) {
    close();
    discardAttempts();
    navigate(buildChatUrl({ prompt: query }));
  }

  function openPage(page: InternalPageHit) {
    close();
    discardAttempts();
    if (page.external) {
      window.open(page.url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(page.url);
  }

  return (
    <div className={topBarStyles.search} role="search" data-testid={E2eTestElementId.DESKTOP_SEARCH}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={open}
      >
        <InterfaceSearch className={styles.triggerIcon} aria-hidden="true" />
        <span className={styles.triggerHint}>{searchPlaceholder(variant, canSearchProducts)}</span>
      </button>
      <UnifiedShellSearchDialog
        isOpen={isOpen}
        // A search the user typed into and walked away from is a search that found nothing useful,
        // and that is worth recording.
        onClose={() => {
          sendAttempts();
          close();
        }}
        anchor={variant === 'bar' ? anchor : undefined}
        query={input}
        onQueryChange={setInput}
        filter={filter}
        onFilterChange={setFilter}
        filters={filters}
        everythingScope={everythingScope}
        placeholder={
          teamDetails ? 'Search every type, or an internal page' : searchPlaceholder('bar', canSearchProducts)
        }
        emptyHint={emptyHint(teamDetails, canSearchProducts)}
        canAskAi={!chatHidden}
        teamDetails={isTeam ? teamDetails : undefined}
        onTeamDetailsChange={changeTeamDetails}
        onOpenHit={openHit}
        onOpenPage={openPage}
        onAsk={ask}
      />
    </div>
  );
}

/**
 * Where the field is, for the panel to open over. Nothing when the field is not laid out (the bar's
 * instance is `display: none` below `md`), so the panel takes its fallback place instead.
 */
function measureAnchor(field: HTMLElement | null): SearchPanelAnchor | undefined {
  if (field === null) {
    return undefined;
  }
  const box = field.getBoundingClientRect();
  if (box.width === 0) {
    return undefined;
  }
  return { top: box.top, right: document.documentElement.clientWidth - box.right };
}

/** What the panel's list says before anything searchable is typed. */
function emptyHint(teamDetails: boolean, canSearchProducts: boolean) {
  if (teamDetails) {
    return 'Type a name to search every type.';
  }
  return canSearchProducts
    ? 'Type a name to search companies, funds, portfolios and products.'
    : 'Type a name to search companies, funds and portfolios.';
}

const TEAM_DETAILS_STORAGE_KEY = 'unifiedShellSearch.teamDetails';

/**
 * The team switch, remembered in this browser so a team member who wants the details does not turn
 * them on for every search. Storage can be unavailable (a private window, blocked site data), and
 * then the switch simply starts off.
 */
function useStoredTeamDetails(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState(() => {
    try {
      return localStorage.getItem(TEAM_DETAILS_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const store = (next: boolean) => {
    setValue(next);
    try {
      localStorage.setItem(TEAM_DETAILS_STORAGE_KEY, String(next));
    } catch {
      // Not remembered, which only costs the next search a click.
    }
  };
  return [value, store];
}

/**
 * Where the field is mounted: the top bar from `md` up, or the phone drawer below it, which the
 * bar hands the search to (design/40-shell-and-pages.md, "Top bar").
 */
type SearchVariant = 'bar' | 'drawer';

/**
 * The hint in the field. In the drawer it is the one word: the drawer caps at 20rem, and on a phone
 * the scoped sentence the bar shows does not fit and is cut off mid-word (the PLT-6945 audit, at
 * 390px). The magnifier and the word say what the control is.
 */
function searchPlaceholder(variant: SearchVariant, canSearchProducts: boolean) {
  if (variant === 'drawer') {
    return 'Search';
  }
  return canSearchProducts ? 'Search for products, companies, or funds' : 'Search for companies or funds';
}
